import { lazy } from "react";
import { Navigate } from "react-router-dom";
import MyCoursesIcon from "@assets/SVGIcons/MyCoursesIcon.web";
import DashboardIcon from "@assets/SVGIcons/DashboardIcon.web";
import RolesPermissionsIcon from "@assets/SVGIcons/RolesPermissionsIcon.web";
import AppearanceMngIcon from "@assets/SVGIcons/AppearanceMngIcon.web";
import UserIcon from "@assets/SVGIcons/UserIcon.web";
import GearIcon from "@assets/SVGIcons/GearIcon.web";
import MyGradebookIcon from "@assets/SVGIcons/MyGradebookIcon.web";
import { NavigationGroup, NavigationRoute } from ".";
import { isAdminStudentCoursesEnabled } from "./tabVisibility";

const AdminUserManagement = lazy(() => import("@components/pages/adminWorkSpace/user-management/AdminUserManagement"));
const AdminUserManagementStudentParents = lazy(() => import("@components/pages/adminWorkSpace/user-management/tabs/Students&ParentsTab"));
const AdminUserManagementTeachersParentTeachers = lazy(() => import("@components/pages/adminWorkSpace/user-management/tabs/Teachers&ParentsTeachers"));
const AdminUserManagementOthers = lazy(() => import("@components/pages/adminWorkSpace/user-management/tabs/OthersTab"));
const AdminCreateUser = lazy(() => import("@components/pages/adminWorkSpace/user-management/AdminCreateUser"));
const AdminUpdateUser = lazy(() => import("@components/pages/adminWorkSpace/user-management/AdminUpdateUser"));
const AdminRolesPermissions = lazy(() => import("@components/pages/adminWorkSpace/@roles-permissions/AdminRolesPermissions"));
const AdminAppearanceManagement = lazy(() => import("@components/pages/adminWorkSpace/appearance-management/AdminAppearanceManagement"));
const AdminAppearanceBranding = lazy(() => import("@components/pages/adminWorkSpace/appearance-management/tabs/AdminAppearanceBranding"));
const AdminAppearanceThemes = lazy(() => import("@components/pages/adminWorkSpace/appearance-management/tabs/AdminAppearanceThemes"));
const AdminAppearanceRangeColors = lazy(() => import("@components/pages/adminWorkSpace/appearance-management/tabs/AdminAppearanceRangeColors"));
const AddNewRole = lazy(() => import("@components/pages/adminWorkSpace/@roles-permissions/actions/AddNewRole"));
const EditViewRole = lazy(() => import("@components/pages/adminWorkSpace/@roles-permissions/actions/EditViewRole"));

const AdminDashboard = lazy(() => import("@components/pages/adminWorkSpace/dashboard/AdminDashboard"));
const AdminDashboardStatistics = lazy(() => import("@components/pages/adminWorkSpace/dashboard/tabs/AdminDashboardStatistics"));
const AdminDashboardReports = lazy(() => import("@components/pages/adminWorkSpace/dashboard/tabs/AdminDashboardReports"));
const AdminSetup = lazy(() => import("@components/pages/adminWorkSpace/setup/AdminSetup"));
const SetupSchoolYear = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupSchoolYear"));
const SetupGrade = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupGrade"));
const SetupSubjects = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupSubjects"));
const SetupCurriculum = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupCurriculum"));
const SetupClassManagement = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupClassManagement"));
const SetupParentTeacherAssignment = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupParentTeacherAssignment"));
const SetupGradebook = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupGradebook"));
const SetupSchoolCalendar = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupCalendars"));
const SetupGradingComponents = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupGradingComponents"));
const SetupCustomFields = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupCustomFields"));
const SetupEvents = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupEvents"));

const SetupGradeBookDetailsContextProvider = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupGradeBookDetails/context/SetupGradeBookDetailsContext"));
const SetupGradebookDetails = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupGradeBookDetails/SetupGradeBookDetails"));
const SetupGradebookTemplate = lazy(() => import("@components/pages/adminWorkSpace/setup/tabs/SetupGradeBookDetails/SetupGradeBookTemplate"));
const SetupWithAI = lazy(() => import("@components/pages/adminWorkSpace/setup/ai/SetupWithAI"));
const BuildCourseWithAI = lazy(() => import("@components/pages/adminWorkSpace/course-management/ai/BuildCourseWithAI"));

const CourseManagement = lazy(() => import("@components/pages/adminWorkSpace/course-management/CourseManagement"));
const CourseManagementTeacherCourses = lazy(() => import("@components/pages/adminWorkSpace/course-management/tabs/TeacherCourses"));
const CourseManagementCatalogs = lazy(() => import("@components/pages/adminWorkSpace/course-management/tabs/Catalogs"));
const CourseManagamentCourseRequests = lazy(() => import("@components/pages/adminWorkSpace/course-management/CourseRequests"));
const CourseManagementCatalogCourses = lazy(() => import("@components/pages/adminWorkSpace/course-management/tabs/CatalogCourses"));
const CourseManagementCourseJourney = lazy(() => import("@components/pages/adminWorkSpace/course-management/CourseJourney"));
// Phase 5 v2 (2026-06-02): admin approval queue for teacher/PT-submitted catalog courses.
const CourseManagementCatalogApprovals = lazy(() => import("@components/pages/adminWorkSpace/course-management/CatalogApprovals"));

const AdminProfile = lazy(() => import("@components/pages/adminWorkSpace/profile/AdminProfile"));
const AdminBugReports = lazy(() => import("@components/pages/adminWorkSpace/bug-reports/AdminBugReports"));
const AdminDeletedItems = lazy(() => import("@components/pages/adminWorkSpace/deleted-items/AdminDeletedItems"));
const GradeBooks = lazy(() => import("@components/pages/registrarWorkSpace/gradebooks/GradeBooks"));
const GradeBookDetails = lazy(() => import("@components/pages/registrarWorkSpace/gradebooks/GradeBookDetails"));
const GradeBookGradesTab = lazy(() => import("@components/pages/registrarWorkSpace/gradebooks/tabs/GradeBookGradesTab"));
const GradeBookAttendancesTab = lazy(() => import("@components/pages/registrarWorkSpace/gradebooks/tabs/GradeBookAttendanceTab"));

const STUDENT_COURSES_ENABLED = isAdminStudentCoursesEnabled(import.meta.env);
// KnowledgeGraphPage import removed per Chronicle boundary (2026-04-15).
// Component preserved as Chronicle incubator material.

export const adminRoutes: NavigationRoute[] = [
  {
    index: true,
    label: "index",
    component: <AdminDashboard />,
  },
  {
    label: "Grade Book Details",
    path: "grading/:student_id/:grade_level_id",
    component: <GradeBookDetails />,
    children: [
      {
        index: true,
        component: <GradeBookGradesTab />,
      },
      {
        path: "attendance-behavior",
        component: <GradeBookAttendancesTab />,
      },
    ],
  },
  {
    path: "setup/gradebook/:gradebook_id",
    label: "Gradebook Details",
    component: <SetupGradeBookDetailsContextProvider />,
    children: [
      {
        index: true,
        component: <SetupGradebookDetails />,
      },
      {
        path: "template/:template",
        component: <SetupGradebookTemplate />
      }
    ],
  },
  {
    path: "profile",
    label: "profile",
    component: <AdminProfile />,
  },
  {
    path: "roles-permissions/create",
    label: "add new role",
    component: <AddNewRole />,
  },
  {
    path: "roles-permissions/:role_id",
    label: "edit role",
    component: <Navigate to="view" />,
  },
  {
    path: "roles-permissions/:role_id/edit",
    label: "edit role",
    component: <EditViewRole />,
  },
  {
    path: "roles-permissions/:role_id/view",
    label: "view role",
    component: <EditViewRole />,
  },
  {
    path: "user-management/create/:role",
    component: <AdminCreateUser />,
  },
  {
    path: "user-management/update/:id",
    component: <AdminUpdateUser />,
  },
  {
    path: "bugs",
    label: "Bug Reports",
    component: <AdminBugReports />,
  },
  {
    path: "course-management/requests",
    component: <Navigate to="for-approval" />,
  },
  {
    path: "course-management/requests/:status",
    label: "Course Requests",
    component: <CourseManagamentCourseRequests />,
  },
  {
    path: "course-management/catalogs/:catalog_id",
    label: "Catalog Courses",
    component: <CourseManagementCatalogCourses />,
  },
  {
    path: "course-management/:course_id/journey/:topic_id?/:lesson_id?/:activity_id?",
    label: "Course Journey",
    component: <CourseManagementCourseJourney />,
  },
  // "students/:student_id/knowledge-graph" route removed per Chronicle boundary
  // (2026-04-15). Becomes a Chronicle section feature when Chronicle is enabled.
  {
    path: "setup/ai",
    label: "Set up with AI",
    component: <SetupWithAI />,
  },
  {
    path: "course-management/ai",
    label: "Build course with AI",
    component: <BuildCourseWithAI />,
  },
];

export const adminNavigations: NavigationGroup[] = [
  {
    title: "Getting Started",
    routes: [
      {
        label: "dashboard",
        component: <AdminDashboard />,
        icon: <DashboardIcon fontSize="small" />,
        path: "dashboard",
        children: [
          {
            index: true,
            component: <AdminDashboardStatistics />,
          },
          {
            path: "reports",
            component: <AdminDashboardReports />,
          },
        ],
      },
    ],
  },
  {
    title: "Manage Hub",
    routes: [
      {
        path: "setup",
        label: "setup",
        component: <AdminSetup />,
        icon: <GearIcon fontSize="small" />,
        children: [
          {
            index: true,
            component: <Navigate to="school-year" />,
          },
          {
            path: "school-year",
            component: <SetupSchoolYear />,
            label: "School Year",
          },
          {
            path: "grade-levels",
            component: <SetupGrade />,
            label: "Grade Levels",
          },
          {
            path: "subjects",
            component: <SetupSubjects />,
            label: "Subjects",
          },
          {
            path: "curriculum",
            component: <SetupCurriculum />,
            label: "Curriculum",
          },
          {
            path: "class-management",
            component: <SetupClassManagement />,
            label: "Class Management",
          },
          {
            path: "parent-teacher-assignment",
            component: <SetupParentTeacherAssignment />,
            label: "Parent Teacher Assignment",
          },
          // Teacher Assignment retired 2026-06-03: teacher↔class assignment is
          // now owned by Class Management (regular classes) + Parent-Teacher
          // Assignment (homeschool classes). It duplicated class_teachers via a
          // parallel teacher_assignments table.
          {
            path: "gradebook",
            component: <SetupGradebook />,
            label: "Grade Book",
          },
          {
            path: "grading-components",
            component: <SetupGradingComponents />,
            label: "Grading Components",
          },
          {
            path: "custom-fields",
            component: <SetupCustomFields />,
            label: "Custom Fields",
          },
          {
            path: "school-calendar",
            component: <SetupSchoolCalendar />,
            label: "School Calendar",
          },
          {
            path: "events",
            component: <SetupEvents />,
            label: "Events",
          },
        ],
      },
      {
        path: "user-management",
        label: "user-management",
        component: <AdminUserManagement />,
        icon: <UserIcon fontSize="small" />,
        children: [
          {
            index: true,
            component: <Navigate to="students-parents" />,
          },
          {
            path: "students-parents",
            component: <AdminUserManagementStudentParents />,
            label: "Students/Parents",
          },
          {
            path: "teachers-parent-teachers",
            component: <AdminUserManagementTeachersParentTeachers />,
            label: "Teachers/Parent-Teachers",
          },
          {
            path: "others",
            component: <AdminUserManagementOthers />,
            label: "Others",
          },
        ],
      },
      {
        path: "course-management",
        label: "course management",
        component: <CourseManagement />,
        icon: <MyCoursesIcon fontSize="medium" />,
        children: [
          {
            index: true,
            component: <Navigate to="courses" />,
          },
          {
            path: "courses",
            label: "Teacher Courses",
            component: <CourseManagementTeacherCourses />,
          },
          {
            path: "catalogs",
            label: "Catalogs",
            component: <CourseManagementCatalogs />,
          },
          {
            path: "catalogs/:catalog_id",
            label: "Catalogs",
            component: <CourseManagementCatalogCourses />,
          },
          {
            path: "catalog-approvals",
            label: "Catalog Approvals",
            component: <CourseManagementCatalogApprovals />,
          },
        ],
      },
      {
        path: "grading",
        label: "Grading",
        component: <GradeBooks />,
        icon: <MyGradebookIcon fontSize="medium" />,
      },
      {
        path: "roles-permissions",
        label: "roles & permissions",
        component: <AdminRolesPermissions />,
        icon: <RolesPermissionsIcon fontSize="medium" />,
      },
      {
        path: "bugs",
        label: "Bug Reports",
        component: <AdminBugReports />,
        icon: <GearIcon fontSize="small" />,
      },
      ...(STUDENT_COURSES_ENABLED
        ? [
            {
              path: "deleted-items",
              label: "Deleted items",
              component: <AdminDeletedItems />,
              icon: <GearIcon fontSize="small" />,
            },
          ]
        : []),
      {
        path: "appearance-management",
        label: "appearance-management",
        component: <AdminAppearanceManagement />,
        icon: <AppearanceMngIcon fontSize="medium" />,
        children: [
          {
            index: true,
            component: <Navigate to="branding" />,
          },
          {
            path: "branding",
            label: "Branding",
            component: <AdminAppearanceBranding />,
          },
          {
            path: "appearance-and-themes",
            label: "Appearance & Themes",
            component: <AdminAppearanceThemes />,
          },
          {
            path: "subjects",
            label: "Subjects",
            component: <SetupSubjects />,
          },
          {
            path: "progress-range-and-colors",
            label: "Progress Range & Color",
            component: <AdminAppearanceRangeColors />,
          },
        ],
      },
    ],
  },
];

export default adminNavigations;
